import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import IdeaCard from "@/components/IdeaCard";
import GroveTour from "@/components/GroveTour";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, MapPin, Sprout } from "lucide-react";

const CATS = ["All", "Ideas", "Recipes", "Home", "Style", "Travel/Places", "Lifestyle", "Parenting/Family", "Wellness", "Faith", "Trusted"];

// Small debounce hook
function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function Grove() {
  const { user } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [category, setCategory] = useState("All");
  // 'all' = Everyone feed (global chronological). 'following' = My Grove
  // (only people I follow, chronological). Preference is remembered per
  // browser so a user's default sticks between sessions — but signed-out
  // visitors always start on Everyone, since My Grove has no meaning there.
  const [tab, setTab] = useState(() => {
    if (typeof window === "undefined") return "all";
    try {
      const saved = localStorage.getItem("willoah.grove.tab.v1");
      return saved === "following" || saved === "all" ? saved : "all";
    } catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("willoah.grove.tab.v1", tab); } catch {}
  }, [tab]);
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(true);
  const [locSuggestions, setLocSuggestions] = useState([]);
  const [locFocused, setLocFocused] = useState(false);
  const debouncedQ = useDebounced(query, 350);
  const debouncedLoc = useDebounced(location, 400);
  const debouncedLocRaw = useDebounced(location, 200); // faster for typeahead

  // Location typeahead: fetch matching known city strings
  useEffect(() => {
    const q = debouncedLocRaw.trim();
    if (!q) { setLocSuggestions([]); return; }
    let cancelled = false;
    api.get("/locations/autocomplete", { params: { q } })
      .then(({ data }) => { if (!cancelled) setLocSuggestions(data.suggestions || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [debouncedLocRaw]);

  // Load the set of already-saved idea ids so cards can render the correct bookmark state
  useEffect(() => {
    if (!user) { setSavedIds(new Set()); return; }
    api.get("/saves/ids").then(({ data }) => setSavedIds(new Set(data.ids || []))).catch(() => {});
  }, [user]);

  const onSaveChange = (id, next) => {
    setSavedIds((prev) => {
      const s = new Set(prev);
      if (next) s.add(id); else s.delete(id);
      return s;
    });
  };

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      try {
        // Unified endpoint — the `feed` param routes both tabs through
        // /api/ideas so category / q / location filter server-side in
        // BOTH cases. My Grove used to hit a legacy endpoint and filter
        // client-side; that lost server-side location filtering and
        // silently degraded to author_name substring matching.
        const params = {};
        if (tab === "following" && user) params.feed = "following";
        if (category !== "All") params.category = category;
        if (debouncedQ.trim()) params.q = debouncedQ.trim();
        if (debouncedLoc.trim()) params.location = debouncedLoc.trim();
        const { data } = await api.get("/ideas", { params });
        setIdeas(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [category, debouncedQ, debouncedLoc, tab, user]);

  const showEmpty = useMemo(() => !loading && ideas.length === 0, [loading, ideas]);

  // First-time signed-in visitor: shown a warm welcome + primary "Plant an idea"
  // CTA above the feed until they plant their first branch. `has_ever_planted`
  // is populated by /auth/me on session bootstrap. Once true it stays true —
  // no localStorage flag needed, the DB is source of truth.
  const showFirstBranchInvite = !!user && user.has_ever_planted === false;

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-24 overflow-x-hidden">
      <GroveTour />

      {showFirstBranchInvite && (
        <div
          data-testid="first-branch-invite"
          className="mb-8 rounded-3xl border border-[#4E5A3D]/30 bg-gradient-to-br from-[#4E5A3D]/10 via-[#B79256]/10 to-[#FBF8F1] px-6 py-6 sm:px-8 sm:py-7 flex flex-col md:flex-row md:items-center gap-5"
        >
          <div className="flex items-start gap-4 flex-1">
            <div className="w-11 h-11 rounded-full bg-[#4E5A3D]/15 border border-[#4E5A3D]/25 flex items-center justify-center shrink-0">
              <Sprout className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.75} />
            </div>
            <div>
              <h2 className="font-serif-display text-2xl text-[#2A2823] leading-tight">
                Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""} — start with one seed you'd share with a friend.
              </h2>
              <p className="text-sm text-[#2A2823]/65 mt-1.5 leading-relaxed">
                An outfit you own, a recipe you make, a place you visited, a small thing that works. One photo, one honest sentence — that's a seed.
              </p>
            </div>
          </div>
          <Link
            to="/plant"
            data-testid="first-branch-plant-cta"
            className="inline-flex items-center gap-2 rounded-full h-11 px-6 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] transition-colors shrink-0 shadow-sm"
          >
            <Sprout className="w-4 h-4" strokeWidth={2}/>
            <span className="accent-label text-[#FBF8F1]">Plant your first seed</span>
          </Link>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <span className="accent-label text-[#B79256]">the feed</span>
          <h1 className="font-serif-display text-5xl sm:text-6xl text-[#2A2823] mt-3 leading-none">The Grove</h1>
          <p className="text-[#2A2823]/60 mt-3 max-w-lg">Honest ideas from real people, newest first. Sponsored content, if any, is always clearly labeled.</p>
        </div>
        {user && (
          <div className="inline-flex rounded-full border border-[#B79256]/35 p-1 bg-white/50" data-testid="feed-tabs">
            <button
              data-testid="tab-my-grove"
              onClick={() => setTab("following")}
              className={`accent-label px-4 py-1.5 rounded-full transition-colors ${tab === "following" ? "bg-[#4E5A3D] text-[#FBF8F1]" : "text-[#2A2823]/70 hover:text-[#2A2823]"}`}
            >
              My Grove
            </button>
            <button
              data-testid="tab-everyone"
              onClick={() => setTab("all")}
              className={`accent-label px-4 py-1.5 rounded-full transition-colors ${tab === "all" ? "bg-[#4E5A3D] text-[#FBF8F1]" : "text-[#2A2823]/70 hover:text-[#2A2823]"}`}
            >
              Everyone
            </button>
          </div>
        )}
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#2A2823]/40 absolute left-4 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
          <Input
            data-testid="grove-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search seeds by word, feeling, or name"
            className="pl-10 pr-10 h-11 rounded-full border-[#B79256]/40 bg-white/70 focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D]"
          />
          {query && (
            <button
              data-testid="grove-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2823]/40 hover:text-[#2A2823]"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" strokeWidth={1.75} />
            </button>
          )}
        </div>
        <div className="relative flex items-center gap-2 max-w-md">
          <div className="relative flex-1">
            <MapPin className="w-4 h-4 text-[#2A2823]/40 absolute left-4 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
            <Input
              data-testid="grove-location-input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onFocus={() => setLocFocused(true)}
              onBlur={() => setTimeout(() => setLocFocused(false), 150)}
              placeholder="Near a city (e.g. Nashville)"
              className="pl-10 pr-10 h-11 rounded-full border-[#B79256]/40 bg-white/70 focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D]"
              autoComplete="off"
            />
            {location && (
              <button
                data-testid="grove-location-clear"
                onClick={() => setLocation("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2823]/40 hover:text-[#2A2823]"
                aria-label="Clear location"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            )}
            {locFocused && locSuggestions.length > 0 && location.trim() && (
              <div
                data-testid="location-suggestions"
                className="absolute z-30 top-full mt-2 left-0 right-0 rounded-2xl border border-[#B79256]/30 bg-[#FBF8F1]/95 backdrop-blur-xl shadow-[0_10px_40px_-12px_rgba(42,40,35,0.25)] overflow-hidden"
                role="listbox"
              >
                {locSuggestions.map((s) => (
                  <button
                    key={s.location}
                    type="button"
                    role="option"
                    data-testid={`location-suggestion`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setLocation(s.location); setLocFocused(false); }}
                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm text-[#2A2823] hover:bg-[#B79256]/12 transition-colors"
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <MapPin className="w-3.5 h-3.5 text-[#4E5A3D] shrink-0" strokeWidth={1.75} />
                      <span className="truncate">{s.location}</span>
                    </span>
                    <span className="text-xs text-[#2A2823]/45">
                      {s.count} sharer{s.count === 1 ? "" : "s"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {user?.location && !location && (
            <button
              type="button"
              data-testid="grove-near-me"
              onClick={() => setLocation(user.location)}
              className="whitespace-nowrap inline-flex items-center gap-1.5 rounded-full h-11 px-4 border border-[#B79256]/40 bg-white/70 text-[#2A2823]/70 hover:text-[#2A2823] hover:bg-white transition-colors accent-label"
              title={`Filter to ${user.location}`}
            >
              <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
              near me
            </button>
          )}
        </div>
      </div>
      <div className="mb-6">
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`accent-label px-4 py-2 rounded-full border transition-colors ${category === c
                ? "border-[#4E5A3D] bg-[#4E5A3D] text-[#FBF8F1]"
                : "border-[#B79256]/40 text-[#2A2823]/70 hover:text-[#2A2823] hover:bg-white/80"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-3xl bg-white border border-[#B79256]/20 h-[430px] animate-pulse" />
          ))}
        </div>
      ) : showEmpty ? (
        <div data-testid="empty-grove" className="text-center py-20 px-6 border border-dashed border-[#B79256]/40 rounded-3xl bg-[#B79256]/5">
          <div className="w-14 h-14 rounded-full bg-[#4E5A3D]/12 mx-auto flex items-center justify-center mb-5">
            <Sprout className="w-6 h-6 text-[#4E5A3D]" strokeWidth={1.5} />
          </div>
          <p className="font-serif-display text-3xl text-[#4E5A3D]">
            {tab === "following"
              ? "Your Grove is quiet."
              : query
              ? `Nothing matched "${query}".`
              : "The Grove is quiet — start with one seed you'd share with a friend."}
          </p>
          <p className="text-[#2A2823]/60 mt-3 max-w-md mx-auto">
            {tab === "following"
              ? "Follow a few people to grow it. Their seeds will land here, newest first — nothing quietly bumped up."
              : query
              ? "Try a different word — or plant the first seed that answers it."
              : "An outfit you own, a recipe you make, a place you went. One photo, one honest sentence."}
          </p>
          {tab === "following" && (
            <button
              type="button"
              data-testid="empty-my-grove-explore-cta"
              onClick={() => setTab("all")}
              className="mt-6 inline-flex items-center gap-2 rounded-full h-11 px-6 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] transition-colors shadow-sm"
            >
              <span className="accent-label text-[#FBF8F1]">Explore Everyone</span>
            </button>
          )}
          {user && tab !== "following" && (
            <Link
              to="/plant"
              data-testid="empty-grove-plant-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full h-11 px-6 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] transition-colors shadow-sm"
            >
              <Sprout className="w-4 h-4" strokeWidth={2}/>
              <span className="accent-label text-[#FBF8F1]">Plant a seed</span>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {ideas.map((idea, i) => (
            <IdeaCard key={idea.regrow_id ? `rg-${idea.regrow_id}` : idea.id} idea={idea} index={i} savedIds={savedIds} onSaveChange={onSaveChange} />
          ))}
        </div>
      )}
    </main>
  );
}
